import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CURRENT_USER } from '../../map/MapContext';
import { ChevronRightIcon, FolderIcon, MoreIcon, SearchIcon } from '../../components/icons/Icons';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { fetchContent, fetchContentDownloadUrl, type ContentItem } from '../../api/content';
import './content-explorer.css';

export const CONTENT_ROOT_LABEL = 'All content';

// Per-content-type badge colors — semantic (one per format), so they live here rather than in
// the theme tokens. All chosen for >= 4.5:1 with the white badge text.
const TYPE_COLORS: Record<string, string> = {
  'image/tiff': '#2f7d4f',
  'application/pdf': '#c53030',
  'text/csv': '#3b6fd6',
  'image/jpeg': '#a16207',
  'image/png': '#a16207',
};
const TYPE_COLOR_FALLBACK = '#6b7280';

/** A short display label from a MIME content_type, e.g. "image/tiff" -> "TIFF". Falls back to
 * the provider name (r2/farmos/erpnext) when there's no content_type at all — an external link
 * has no file format of its own. */
function typeLabel(item: ContentItem): string {
  if (!item.content_type) return item.provider.toUpperCase();
  const subtype = item.content_type.split('/')[1] ?? item.content_type;
  return subtype.toUpperCase();
}

function FileTypeBadge({ item }: { item: ContentItem }) {
  const color = (item.content_type && TYPE_COLORS[item.content_type]) ?? TYPE_COLOR_FALLBACK;
  return (
    <span className="content-file-badge" style={{ background: color }}>
      {typeLabel(item)}
    </span>
  );
}

type ContentState =
  | { status: 'loading' }
  | { status: 'ready'; items: ContentItem[] }
  | { status: 'error'; message: string };

/**
 * Row "..." menu. Open/Download both call GET /api/v1/content/{id}/download for a fresh,
 * short-lived signed URL (ADR-0005) rather than reusing any URL fetched earlier — the list
 * endpoint never returns one. This is a read-only viewer (files arrive in R2 out-of-band), so
 * there is genuinely no rename/move/delete capability here, now or later.
 */
function RowMenu({ item, onClose }: { item: ContentItem; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onDocPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  async function open() {
    setBusy(true);
    try {
      const { url } = await fetchContentDownloadUrl(item.content_item_id);
      window.open(url, '_blank', 'noopener');
    } catch {
      // Signing can fail (503 if R2 isn't configured, 404 if the item vanished) — the row menu
      // has no place to show a persistent error, so this is a silent no-op rather than a crash.
    } finally {
      setBusy(false);
      onClose();
    }
  }

  async function download() {
    setBusy(true);
    try {
      const { url } = await fetchContentDownloadUrl(item.content_item_id);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.title;
      a.click();
    } catch {
      // Same as open() above.
    } finally {
      setBusy(false);
      onClose();
    }
  }

  return (
    <div className="content-row-menu" role="menu" ref={ref}>
      <button type="button" className="content-row-menu-item" role="menuitem" disabled={busy} onClick={open}>
        Open
      </button>
      <button type="button" className="content-row-menu-item" role="menuitem" disabled={busy} onClick={download}>
        Download
      </button>
      <div className="content-row-menu-divider" />
      <button type="button" className="content-row-menu-item" role="menuitem" disabled title="Not available yet">
        Rename
      </button>
      <button type="button" className="content-row-menu-item" role="menuitem" disabled title="Not available yet">
        Move to folder
      </button>
      <button
        type="button"
        className="content-row-menu-item content-row-menu-danger"
        role="menuitem"
        disabled
        title="Not available yet"
      >
        Delete
      </button>
    </div>
  );
}

/**
 * Content page's file/folder browser (ROADMAP.md v0.6.4). Reads real catalogue records from
 * GET /api/v1/content — whatever system holds each item (an R2 object, or a link into farmOS/
 * ERPNext's own UI) — rather than pages/HomePage/contentFiles.ts's old hardcoded mock. Folders
 * are derived from the items themselves (each item's own `folder` field), not a fixed list, so
 * a provider-side registration is enough to make a new folder appear — no frontend deploy.
 * Deliberately no upload or create-folder UI here, ever, not even a disabled stub button (unlike
 * the Day/Week/Year case, this is permanently out of scope, not "not yet" — see README.md).
 */
export function ContentExplorer() {
  const [state, setState] = useState<ContentState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchContent()
      .then((items) => {
        if (!cancelled) setState({ status: 'ready', items });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : 'Unknown error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const items = state.status === 'ready' ? state.items : [];

  const folders = useMemo(() => {
    const names = new Set<string>();
    for (const item of items) {
      if (item.folder) names.add(item.folder);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const itemsInScope = useMemo(() => {
    const base = selectedFolder === null ? items : items.filter((i) => i.folder === selectedFolder);
    if (!query.trim()) return base;
    const q = query.toLowerCase();
    return base.filter((i) => i.title.toLowerCase().includes(q));
  }, [items, selectedFolder, query]);

  function toggleRow(id: string) {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedRows((prev) =>
      prev.size === itemsInScope.length && itemsInScope.length > 0
        ? new Set()
        : new Set(itemsInScope.map((i) => i.content_item_id))
    );
  }

  if (state.status === 'loading') {
    return <p className="home-loading" role="status">Loading content…</p>;
  }

  if (state.status === 'error') {
    return (
      <div className="home-error" role="alert">
        <p>Couldn&rsquo;t load content. {state.message}</p>
        <button type="button" className="app-retry-btn" onClick={retry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="content-explorer">
      <aside className="content-tree">
        <button
          type="button"
          className={`content-tree-root${selectedFolder === null ? ' active' : ''}`}
          onClick={() => setSelectedFolder(null)}
        >
          {CONTENT_ROOT_LABEL}
        </button>
        <ul className="content-tree-list">
          {folders.map((folder) => (
            <li key={folder}>
              <button
                type="button"
                className={`content-tree-item${selectedFolder === folder ? ' active' : ''}`}
                onClick={() => setSelectedFolder(folder)}
              >
                <FolderIcon size={13} />
                {folder}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="content-main">
        <div className="content-main-header">
          <div className="content-breadcrumb">
            <button type="button" className="content-breadcrumb-crumb" onClick={() => setSelectedFolder(null)}>
              {CONTENT_ROOT_LABEL}
            </button>
            {selectedFolder && (
              <>
                <ChevronRightIcon size={10} />
                <span className="content-breadcrumb-current">{selectedFolder}</span>
              </>
            )}
          </div>
          <div className="content-main-controls">
            <div className="content-search">
              <SearchIcon />
              <input
                type="text"
                placeholder="Search this folder"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search content"
              />
            </div>
            <Tooltip heading="Access" description="Only you can see this — sharing isn't available yet.">
              <span className="workspace-avatar">{CURRENT_USER}</span>
            </Tooltip>
          </div>
        </div>

        <table className="content-table">
          <thead>
            <tr>
              <th className="content-col-checkbox">
                <input
                  type="checkbox"
                  checked={itemsInScope.length > 0 && selectedRows.size === itemsInScope.length}
                  onChange={toggleSelectAll}
                  aria-label="Select all files"
                />
              </th>
              <th>Name</th>
              <th>Type</th>
              <th>Provider</th>
              <th>Access</th>
              <th className="content-col-menu" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {itemsInScope.map((item) => (
              <tr key={item.content_item_id} className={selectedRows.has(item.content_item_id) ? 'selected' : undefined}>
                <td className="content-col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(item.content_item_id)}
                    onChange={() => toggleRow(item.content_item_id)}
                    aria-label={`Select ${item.title}`}
                  />
                </td>
                <td className="content-cell-name">
                  <FileTypeBadge item={item} />
                  <span>{item.title}</span>
                </td>
                <td>{typeLabel(item)}</td>
                <td>{item.provider}</td>
                <td>
                  <Tooltip heading="Access" description="Only you can see this — sharing isn't available yet.">
                    <span className="workspace-avatar content-row-avatar">{CURRENT_USER}</span>
                  </Tooltip>
                </td>
                <td className="content-cell-menu">
                  <div className="content-row-menu-wrap">
                    <button
                      type="button"
                      className="content-row-menu-btn"
                      aria-label={`More options for ${item.title}`}
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === item.content_item_id}
                      onClick={() => setOpenMenuId((v) => (v === item.content_item_id ? null : item.content_item_id))}
                    >
                      <MoreIcon />
                    </button>
                    {openMenuId === item.content_item_id && (
                      <RowMenu item={item} onClose={() => setOpenMenuId(null)} />
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {itemsInScope.length === 0 && (
              <tr>
                <td colSpan={6} className="content-empty-row">
                  {query ? `No files match “${query}”.` : 'This folder is empty.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
